/** Shapes of GET /api/v1/whatsnew (Backend/whatsnew.py `page`). */

export interface WnCategory { key: string; label: string; color: string }

export interface WnSettings {
  texts: { title: string; subtitle: string; crawl_label: string; curated_label: string };
  sections: { platform: boolean; new_universities: boolean; changes: boolean; world: boolean };
  period_days: number;
  world_default_days: number;
  show_removed: boolean;
  categories: WnCategory[];
  industries: string[];
}

export interface WnNewUniversity {
  code: string; name: string; country: string; date: string | null; listings: number; path: string | null;
}

export interface WnChange {
  id: string; kind: 'added' | 'removed'; code: string; name: string; country: string; n: number;
  tags: string[]; date: string | null; path: string | null; source: 'load' | 'snapshot' | 'recrawl';
  /** Removed cards: the first titles (Backend REMOVED_TITLES_SHOWN); all of them via the change list. */
  titles?: string[];
}

/** One technology of a change (GET /api/v1/whatsnew/change). Removed ones carry only id + title. */
export interface WnChangeTech {
  id: string; title: string; available: boolean;
  university?: string; area?: string; tags: string[]; saved?: boolean;
}

export interface WnPlatform {
  period_days: number;
  last_run: { at: string | null; label?: string; tech_offers?: number } | null;
  new_universities: WnNewUniversity[];
  new_universities_total: number;
  changes: WnChange[];
  changes_total: number;
  error?: string;
}

export interface WnItem {
  id: number; status: string; category: string; type_label: string; item_date: string; title: string;
  org: string; country: string; summary: string; amount: string; industry: string; source_url: string;
}

export interface WnGroup {
  key: string; label: string; color: string; count: number; items: WnItem[]; more: number;
}

export interface WnWorld {
  counts: Record<string, number>;
  groups: WnGroup[];
  filters: { category: string; industry: string; from: string; to: string };
}

export interface WnPayload {
  settings: WnSettings;
  platform: WnPlatform | null;
  world: WnWorld | null;
  preview?: boolean;
  generated_at?: string;
}

export interface WnQuery { category: string; industry: string; from: string; to: string }

/** Loads the page data for a filter set: { data, error }. */
export type WnFetcher = (q: WnQuery) => Promise<{ data: WnPayload | null; error: string }>;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "Apr 20, 2026" (or "Apr 20" with short). A bare YYYY-MM-DD is a calendar
 *  day, not an instant, so it is never shifted by the time zone. */
export const fmtDay = (v?: string | null, short = false): string => {
  if (!v) return '';
  let y: number, m: number, d: number;
  const bare = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
  if (bare) {
    y = +bare[1]; m = +bare[2] - 1; d = +bare[3];
  } else {
    const t = new Date(v);
    if (isNaN(t.getTime())) return v;
    y = t.getFullYear(); m = t.getMonth(); d = t.getDate();
  }
  return short ? `${MONTHS[m]} ${d}` : `${MONTHS[m]} ${d}, ${y}`;
};

/** A YYYY-MM-DD n days before today (local). */
export const daysAgo = (n: number): string => {
  const t = new Date();
  t.setDate(t.getDate() - n);
  const p = (x: number) => String(x).padStart(2, '0');
  return `${t.getFullYear()}-${p(t.getMonth() + 1)}-${p(t.getDate())}`;
};

/** #RRGGBB at the given alpha, for the type-label chip background. */
export const tint = (hex: string, alpha = 0.14): string => {
  const m = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex || '');
  if (!m) return `rgba(128,101,63,${alpha})`;
  return `rgba(${parseInt(m[1], 16)},${parseInt(m[2], 16)},${parseInt(m[3], 16)},${alpha})`;
};

export const hostOf = (url: string): string => {
  try { return new URL(url).hostname.replace(/^www\./, ''); } catch { return ''; }
};
